/**
 * Input sanitization utilities
 */

export class InputSanitizer {
  // Maximum allowed input lengths
  private static readonly MAX_TEXT_LENGTH = 10000;
  private static readonly MAX_API_KEY_LENGTH = 100;

  // Enhanced dangerous patterns for production security
  private static readonly DANGEROUS_PATTERNS = [
    /<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi,
    /javascript:/gi,
    /on\w+\s*=/gi,
    /data:text\/html/gi,
    /vbscript:/gi,
    /<iframe\b[^>]*>/gi,
    /<object\b[^>]*>/gi,
    /<embed\b[^>]*>/gi,
    /<link\b[^>]*>/gi,
    /<meta\b[^>]*>/gi
  ];

  // Sanitize user text input with enhanced security
  public static sanitizeText(input: string): string {
    if (typeof input !== 'string') {
      throw new Error('Input must be a string');
    }

    // Length validation
    if (input.length > this.MAX_TEXT_LENGTH) {
      throw new Error(`Text input exceeds maximum length of ${this.MAX_TEXT_LENGTH} characters`);
    }

    let sanitized = input
      // Remove null bytes and control characters
      .replace(/\0/g, '')
      .replace(/[\x01-\x08\x0B\x0C\x0E-\x1F\x7F]/g, '');

    // Apply enhanced dangerous pattern removal
    this.DANGEROUS_PATTERNS.forEach(pattern => {
      sanitized = sanitized.replace(pattern, '');
    });

    // Additional XSS prevention
    sanitized = sanitized
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"')
      .replace(/&#x27;/g, "'")
      .replace(/&#x2F;/g, '/')
      .replace(/<[^>]*>/g, ''); // Remove any remaining HTML tags

    return sanitized.trim();
  }

  // Much more permissive API key sanitization - preserve API key integrity
  public static sanitizeApiKey(input: string): string {
    if (typeof input !== 'string') {
      throw new Error('API key must be a string');
    }

    if (input.length > this.MAX_API_KEY_LENGTH) {
      throw new Error(`API key exceeds maximum length of ${this.MAX_API_KEY_LENGTH} characters`);
    }

    // Very minimal sanitization - only remove clearly malicious characters
    // Preserve all valid API key characters including alphanumeric, hyphens, underscores
    const sanitized = input
      .replace(/[<>]/g, '') // Only remove angle brackets
      .replace(/['"`]/g, '') // Remove quotes that could break injection
      .replace(/\s/g, '') // Remove whitespace
      .trim();

    if (sanitized.length === 0) {
      throw new Error('Invalid API key format');
    }

    // More lenient validation - don't throw errors for format issues
    if (!this.isValidApiKeyFormat(sanitized)) {
      console.warn('API key format validation warning (non-blocking)');
      // Don't throw error - just warn, as different services have different formats
    }

    return sanitized;
  }

  private static isValidApiKeyFormat(key: string): boolean {
    // Google API key pattern validation - more accurate pattern
    const googleApiPattern = /^AIza[A-Za-z0-9_-]{35}$/;
    
    // More lenient validation - accept various key formats
    const isGoogleKey = googleApiPattern.test(key);
    const isReasonableLength = key.length >= 20 && key.length <= 100;
    const hasValidChars = /^[A-Za-z0-9_-]+$/.test(key);
    
    return isGoogleKey || (isReasonableLength && hasValidChars);
  }

  // Enhanced JSON response validation with proper security checks
  public static validateJsonResponse(response: any): any {
    if (!response || typeof response !== 'object') {
      throw new Error('Invalid response format');
    }

    // Proper prototype pollution detection - only flag if properties are explicitly set
    if (response.hasOwnProperty('__proto__') || 
        response.hasOwnProperty('constructor') && response.constructor !== Object ||
        response.hasOwnProperty('prototype')) {
      throw new Error('Security violation detected in response');
    }

    const sanitizeValue = (value: any): any => {
      if (typeof value === 'string') {
        // Apply enhanced string sanitization
        let sanitized = value;
        this.DANGEROUS_PATTERNS.forEach(pattern => {
          sanitized = sanitized.replace(pattern, '');
        });
        return sanitized;
      }
      if (typeof value === 'number' || typeof value === 'boolean') {
        return value;
      }
      if (Array.isArray(value)) {
        return value.map(item => sanitizeValue(item));
      }
      if (value && typeof value === 'object') {
        const sanitizedObj: any = {};
        for (const [key, val] of Object.entries(value)) {
          // Sanitize property names
          const sanitizedKey = key.replace(/[<>'"]/g, '').trim();
          if (sanitizedKey && !sanitizedKey.startsWith('__')) {
            sanitizedObj[sanitizedKey] = sanitizeValue(val);
          }
        }
        return sanitizedObj;
      }
      return null;
    };

    return sanitizeValue(response);
  }

  // Relaxed rate limiting for personal API usage
  private static requestCounts = new Map<string, { count: number; resetTime: number; suspicious: boolean }>();

  public static checkRateLimit(identifier: string, maxRequests: number = 50, windowMs: number = 60000): boolean {
    const now = Date.now();
    const key = identifier;
    
    const current = this.requestCounts.get(key);
    
    if (!current || now > current.resetTime) {
      this.requestCounts.set(key, { count: 1, resetTime: now + windowMs, suspicious: false });
      return true;
    }
    
    if (current.count >= maxRequests) {
      current.suspicious = true;
      console.warn(`Rate limit exceeded for: ${identifier}`);
      return false;
    }
    
    current.count++;
    
    // Mark as suspicious if approaching limit quickly
    if (current.count > maxRequests * 0.8) {
      current.suspicious = true;
    }
    
    return true;
  }

  // Security monitoring helper
  public static getSuspiciousActivity(): string[] {
    const suspicious: string[] = [];
    this.requestCounts.forEach((data, key) => {
      if (data.suspicious) {
        suspicious.push(`High request rate detected for: ${key}`);
      }
    });
    return suspicious;
  }
}
